import { Alert, AlertIcon, Box, Button, Flex, Input, Text } from '@chakra-ui/react'

import { FormField } from '../../../components/FormField'
import { isRichTextEmpty, RichTextField } from '../../../components/RichTextField'
import WysiwygContent from '../../../components/WysiwygContent'
import { IFaqFields } from '../../../services/Event/EventService'
import { Faq } from '../../../services/Event/types'

export const baseFaq = (): IFaqFields => ({
  _id: crypto.randomUUID(),
  question: '',
  answer: ''
})

/**
 * Client-side validation mirroring the API: question and answer are required in pair.
 * Returns a `fieldErrors`-shaped map keyed by `${errorPrefix}${index}.question|answer`.
 * An entirely empty row is flagged on both fields (remove it or fill it in).
 */
export const validateFaqs = (faqs: IFaqFields[], errorPrefix = 'faqs.'): Record<string, string[]> => {
  const errors: Record<string, string[]> = {}
  faqs.forEach((faq, index) => {
    const hasQuestion = faq.question.trim() !== ''
    const hasAnswer = !isRichTextEmpty(faq.answer)
    if (!hasQuestion) {
      errors[`${errorPrefix}${index}.question`] = ['A pergunta é obrigatória.']
    }
    if (!hasAnswer) {
      errors[`${errorPrefix}${index}.answer`] = ['A resposta é obrigatória.']
    }
  })
  return errors
}

interface IProps {
  faqs: IFaqFields[]
  onChange: (next: IFaqFields[]) => void
  disabled?: boolean
  /** Field errors keyed by dotted path (e.g. `faqs.0.question`). */
  fieldErrors?: Record<string, string[]>
  /** Prefix for the error keys. Defaults to `faqs.`. */
  errorPrefix?: string
  /** Series FAQs inherited by an event (read-only). Only passed on the event form. */
  inheritedFaqs?: Faq[]
}

/**
 * Add/edit/remove FAQ (question + rich-text answer) pairs. Shared by the event and
 * series forms. On the event form, `inheritedFaqs` shows the series' FAQs read-only,
 * making the additive rule (series FAQs + event FAQs) obvious to the operator.
 */
export function FaqEditor({ faqs, onChange, disabled, fieldErrors, errorPrefix = 'faqs.', inheritedFaqs }: IProps) {
  const add = () => onChange([...faqs, baseFaq()])

  const update = (id: string, field: 'question' | 'answer', value: string) =>
    onChange(faqs.map((f) => (f._id === id ? { ...f, [field]: value } : f)))

  const remove = (id: string) => onChange(faqs.filter((f) => f._id !== id))

  const errorFor = (index: number, field: 'question' | 'answer') =>
    fieldErrors?.[`${errorPrefix}${index}.${field}`]?.[0]

  return (
    <>
      {inheritedFaqs && inheritedFaqs.length > 0 && (
        <Box mb={4}>
          <Alert status="info" borderRadius="md" fontSize="sm" mb={2}>
            <AlertIcon />
            Estas perguntas vêm da série e também serão exibidas neste evento.
          </Alert>
          {inheritedFaqs.map((faq) => (
            <Box key={faq.id} border="1px solid" borderColor="gray.100" borderRadius="md" bg="gray.50" p={3} mb={2}>
              <Text fontWeight="bold" fontSize="sm">
                {faq.question}
              </Text>
              <WysiwygContent content={faq.answer} fontSize="sm" color="gray.600" />
            </Box>
          ))}
        </Box>
      )}

      {faqs.map((faq, index) => (
        <Box key={faq._id} border="1px solid" borderColor="gray.200" borderRadius="md" p={4} mt={3}>
          <Flex justifyContent="space-between" alignItems="center" mb={3}>
            <Text fontWeight="bold" fontSize="sm">
              Pergunta {index + 1}
            </Text>
            <Button size="xs" colorScheme="red" variant="ghost" onClick={() => remove(faq._id)} disabled={disabled}>
              Remover
            </Button>
          </Flex>

          <Flex direction="column" gap={3}>
            <FormField label="Pergunta" error={errorFor(index, 'question')} isRequired>
              <Input
                size="sm"
                value={faq.question}
                onChange={(e) => update(faq._id, 'question', e.target.value)}
                disabled={disabled}
                placeholder="Ex.: Preciso levar material?"
              />
            </FormField>

            <RichTextField
              label="Resposta"
              error={errorFor(index, 'answer')}
              isRequired
              value={faq.answer}
              onChange={(answer) => update(faq._id, 'answer', answer)}
              placeholder="Resposta da pergunta..."
              isReadOnly={disabled}
            />
          </Flex>
        </Box>
      ))}

      <Button size="sm" variant="outline" colorScheme="gray" onClick={add} disabled={disabled} mt={3}>
        + Adicionar pergunta
      </Button>
    </>
  )
}
